'use client';
import React, { useEffect, useRef, useState, useId } from 'react';
import PropTypes from 'prop-types';
import NoSidebar from '../../layouts/NoSidebar';
import Link from 'next/link';
import s from './PublicArticlePage.module.scss';
import { t } from '../../i18n';
import Icon from '../../components/Icon';
import Richtext from '../../components/Richtext/Richtext';

import Marquee from 'react-fast-marquee';
import TableOfContents from '../../components/TableOfContents/TableOfContents';
import TableOfContentsPlain from '../../components/TableOfContentsPlain/TableOfContentsPlain';

const PublicArticlePage = ({
    siteSetting = {},
    text = { richText: '' },
    title = '',
    date = '',
    publishedDate,
    updatedDate,
}) => {
    const {
        address,
        contactEmail,
        fullCompanyName,
        footerLinks = [],
        marqueeText,
    } = siteSetting;
    const id = useId();
    const richtextRef = useRef(null);
    const [headings, setHeadings] = useState([]);
    const [activeHeading, setActiveHeading] = useState('');

    useEffect(() => {
        if (richtextRef.current) {
            const headingElements =
                richtextRef.current.querySelectorAll('h1, h2, h3, h4, h5');
            const headingData = Array.from(headingElements).map((el, index) => {
                return {
                    text: el.textContent,
                    id: `${id}-${index}`,
                    level: el.tagName.toLowerCase(),
                };
            });
            setHeadings(headingData);

            if (headingData.length > 0) {
                setActiveHeading(headingData[0].text);
            }
        }
    }, [text.richText]);

    const handleScrollTo = (headingText) => {
        const targetElement = Array.from(
            richtextRef.current.querySelectorAll('h1, h2, h3, h4, h5')
        ).find((el) => el.textContent === headingText);

        if (targetElement) {
            const offset = 12;
            const targetPosition =
                targetElement.offsetTop -
                richtextRef.current.offsetTop -
                offset;

            richtextRef.current.scrollTo({
                top: targetPosition,
                behavior: 'smooth',
            });
            setActiveHeading(headingText);
        }
    };

    return (
        <div className={s.Root}>
            <Icon name="LogoWhite" className={s.Logo} />
            <div className={s.Container}>
                <div className={s.ArticleSection}>
                    <div className={s.ArticleWrapper}>
                        <div className={s.Left}>
                            <h1 className={s.Title}>{title}</h1>
                            <span className={s.Date}>
                                {`${t('general.updated')} ${updatedDate}`}
                            </span>
                            <div className={s.TableOfContents}>
                                <TableOfContentsPlain
                                    headings={headings}
                                    onScroll={handleScrollTo}
                                    activeHeading={activeHeading}
                                    showIntroduction={false}
                                />
                            </div>
                        </div>
                        <div className={s.Right} ref={richtextRef}>
                            <Richtext text={text} />
                        </div>
                    </div>
                </div>
                <div className={s.Footer}>
                    <div className={s.InfoWrapper}>
                        <div className={s.Column}>
                            <label>{t('general.contact')}</label>
                            <Link
                                href={'mailto:' + contactEmail}
                                className={s.Link}>
                                <span className={s.LinkTitle}>
                                    {contactEmail}
                                </span>
                                <Icon
                                    name="ArrowExternalPink"
                                    className={s.Arrow}
                                />
                            </Link>
                        </div>

                        <address className={s.Column}>
                            <label>{t('general.address')}</label>
                            <Link
                                href={
                                    'https://www.google.com/maps/place/' +
                                    address?.streetAddress
                                }
                                className={s.Link}>
                                <span className={s.LinkTitle}>
                                    {address?.streetAddress}
                                </span>
                                <Icon
                                    name="ArrowExternalPink"
                                    className={s.Arrow}
                                />
                            </Link>
                        </address>

                        <div className={s.ColumnLinks}>
                            <label>{t('general.links')}</label>
                            <div className={s.Links}>
                                {footerLinks.map((link, index) => (
                                    <Link
                                        key={index}
                                        href={link.url}
                                        className={s.Link}>
                                        <span>{link.title}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className={s.ColumnLogo}>
                            <Icon name="LogoWhite" className={s.FooterLogo} />
                        </div>
                    </div>
                    <div className={s.Marquee}>
                        <Marquee speed={90} autoFill>
                            <span className={s.MarqueeText}>{marqueeText}</span>
                        </Marquee>
                    </div>
                </div>
                <div className={s.CompanyName}>
                    <label>{fullCompanyName}</label>
                </div>
            </div>
        </div>
    );
};

PublicArticlePage.propTypes = {
    headline: PropTypes.string.isRequired,
    preamble: PropTypes.string.isRequired,
    loginUrl: PropTypes.string.isRequired,
};

export default NoSidebar(PublicArticlePage);
